import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Percent, Sparkles, Users, TrendingUp, Loader2 } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Fee rate card — shows the authenticated user's current Best-Available
 * platform fee tier sourced from GET /api/me/fee. Lives on the Creator
 * Dashboard and Partner Dashboard so sellers can see in real time that
 * their Founding / Referral benefit is active.
 *
 * Variants:
 *   - compact=true  → inline KPI tile (for KPI grids)
 *   - compact=false → full-width banner (default on dashboard top)
 */
export default function FeeRateCard({ compact = false, className = '' }) {
  const [fee, setFee] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const token = localStorage.getItem('token');
        if (!token) {
          setLoading(false);
          return;
        }
        const res = await axios.get(`${API_URL}/api/me/fee`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setFee(res.data);
      } catch (err) {
        // Non-critical — dashboard works without this tile
        console.debug('FeeRateCard: /me/fee failed (non-fatal):', err?.message || err);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  if (loading) {
    return (
      <div
        className={`bg-neutral-900/50 border border-white/10 rounded-xl p-5 flex items-center justify-center ${className}`}
        data-testid="fee-rate-card-loading"
      >
        <Loader2 className="w-5 h-5 text-neutral-500 animate-spin" />
      </div>
    );
  }

  if (!fee) return null;

  const { rate_percent: pct, tier, expires_at: expiresAt, label, pending } = fee;

  // Founding pre-launch state: clock hasn't started yet (waiting on
  // public launch date). Surface the upcoming start so creators see
  // "0% starts on launch day" instead of a confusing 5% fallback.
  const foundingScheduled = (pending || []).find(
    (p) => p?.tier === 'founding' && p?.status === 'scheduled' && p?.starts_at
  );

  const formatDate = (iso) =>
    new Date(iso).toLocaleDateString('en-US', {
      year: 'numeric', month: 'short', day: 'numeric',
    });
  const daysUntil = (iso) => {
    const ms = new Date(iso).getTime() - Date.now();
    return Math.max(0, Math.ceil(ms / (1000 * 60 * 60 * 24)));
  };
  const theme = tier === 'founding'
    ? { accent: '#00F0FF', Icon: Sparkles, bg: 'from-[#00F0FF]/15 to-[#00FF94]/10', border: 'border-[#00F0FF]/40' }
    : tier === 'referral_tier_4'
      ? { accent: '#00F0FF', Icon: Users, bg: 'bg-[#00F0FF]/10', border: 'border-[#00F0FF]/40' }
      : { accent: '#a3a3a3', Icon: Percent, bg: 'from-neutral-800/40 to-neutral-900/40', border: 'border-white/10' };
  const { accent, Icon, bg, border } = theme;

  const expiresLabel = expiresAt ? formatDate(expiresAt) : null;
  const daysLeft = expiresAt ? daysUntil(expiresAt) : null;
  const showCountdown =
    tier !== 'standard' && expiresAt && daysLeft !== null;

  if (compact) {
    return (
      <div
        className={`bg-gradient-to-br ${bg} border ${border} rounded-xl p-5 hover:brightness-110 transition-all ${className}`}
        data-testid="fee-rate-card-compact"
      >
        <div className="flex items-center gap-2 mb-2" style={{ color: accent }}>
          <Icon className="w-5 h-5" />
          <span className="text-xs uppercase tracking-wider font-semibold">Your fee</span>
        </div>
        <div className="text-3xl font-black" style={{ color: accent }} data-testid="fee-rate-percent">
          {pct}%
        </div>
        <div className="text-[11px] text-neutral-400 mt-2 line-clamp-2" data-testid="fee-rate-label">
          {label}
        </div>
        {showCountdown && (
          <div className="text-[10px] text-neutral-500 mt-1" data-testid="fee-rate-countdown-compact">
            {daysLeft} day{daysLeft === 1 ? '' : 's'} left · until {expiresLabel}
          </div>
        )}
        {!showCountdown && foundingScheduled && (
          <div className="text-[10px] text-[#00FF94] mt-1" data-testid="fee-rate-scheduled-compact">
            0% starts {formatDate(foundingScheduled.starts_at)} ({daysUntil(foundingScheduled.starts_at)}d)
          </div>
        )}
      </div>
    );
  }

  // Full-width banner
  return (
    <div
      className={`relative overflow-hidden bg-gradient-to-r ${bg} border ${border} rounded-2xl p-6 md:p-8 ${className}`}
      data-testid="fee-rate-card-banner"
    >
      <div className="flex items-center gap-4 md:gap-6 flex-wrap">
        <div
          className="w-16 h-16 rounded-2xl flex items-center justify-center shrink-0"
          style={{ backgroundColor: `${accent}22`, border: `1px solid ${accent}44` }}
        >
          <Icon className="w-8 h-8" style={{ color: accent }} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-baseline gap-3 flex-wrap">
            <span className="text-4xl md:text-5xl font-black" style={{ color: accent }} data-testid="fee-rate-percent">
              {pct}%
            </span>
            <span className="text-sm text-neutral-400 uppercase tracking-wider">platform fee</span>
          </div>
          <div className="mt-1 text-base font-medium text-white" data-testid="fee-rate-label">
            {label}
          </div>
          {showCountdown && (
            <div className="mt-2 inline-flex items-center gap-2 flex-wrap" data-testid="fee-rate-countdown">
              <span
                className="inline-flex items-center gap-1.5 text-xs font-semibold rounded-full px-2.5 py-1"
                style={{
                  color: '#00FF94',
                  backgroundColor: '#00FF9415',
                  border: '1px solid #00FF9444',
                }}
              >
                <TrendingUp className="w-3.5 h-3.5" />
                {daysLeft} day{daysLeft === 1 ? '' : 's'} left
              </span>
              <span className="text-xs text-neutral-400">
                {pct}% until {expiresLabel}
              </span>
            </div>
          )}
          {!showCountdown && foundingScheduled && (
            <div
              className="mt-2 inline-flex items-center gap-1.5 text-xs font-semibold rounded-full px-2.5 py-1"
              style={{
                color: '#00FF94',
                backgroundColor: '#00FF9415',
                border: '1px solid #00FF9444',
              }}
              data-testid="fee-rate-scheduled"
            >
              <Sparkles className="w-3.5 h-3.5" />
              0% starts on launch day · {formatDate(foundingScheduled.starts_at)}
              {' · '}
              {daysUntil(foundingScheduled.starts_at)} days
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
